import { FiInfo } from 'react-icons/fi'
import { faFull, faPlain, toman } from 'utilities/helper/format'
import ScoreMeter from './ScoreMeter'

/**
 * One member in the «گیرندهٔ وام» list.
 *
 * The row is ordered the way the decision is made: name and rank, then the
 * score bar, then the two facts that change the answer — what they asked for,
 * and whether they currently owe. Everything else lives behind the (i) or on
 * the next screen; a list a manager has to read carefully is a list they will
 * not read at all.
 *
 * Trouble is stated, never scored away: an overdue member can still be picked
 * (funds lend to people in difficulty on purpose), so the red line informs
 * rather than blocks.
 */
export default function CandidateRow({
    candidate,
    isSelected,
    onSelect,
    onExplain,
}: {
    candidate: ILoanCandidate
    isSelected: boolean
    onSelect: () => void
    onExplain: () => void
}) {
    const { demand } = candidate
    return (
        <div
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={onSelect}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect()
                }
            }}
            className={` w-full cursor-pointer rounded-2xl border bg-white p-3 text-right transition-colors ${
                isSelected
                    ? ' border-brand shadow-card'
                    : ' border-stroke active:bg-canvas'
            }`}
        >
            <div className=" flex items-center justify-between gap-2">
                <p className=" truncate text-sm font-bold text-heading">
                    {candidate.member_name}
                </p>
                <span className=" shrink-0 text-[11px] text-text-secondary">
                    رتبه {faPlain.format(candidate.rank)}
                </span>
            </div>

            <div className=" mt-2 flex items-center gap-2">
                <div className=" flex-1">
                    <ScoreMeter percentile={candidate.percentile} />
                </div>
                {/* its own button, not a nested one — the row is a role=button */}
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={`توضیح امتیاز ${candidate.member_name}`}
                    onClick={(event) => {
                        event.stopPropagation()
                        onExplain()
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            event.stopPropagation()
                            onExplain()
                        }
                    }}
                    className=" -m-1 shrink-0 cursor-pointer p-1 text-text-muted"
                >
                    <FiInfo size={15} />
                </span>
            </div>

            <p className=" mt-2 text-[11px] text-text-secondary">
                سرمایه {toman(candidate.capital)}
                {candidate.loan_outstanding > 0 && (
                    <> · بدهی {toman(candidate.loan_outstanding)}</>
                )}
            </p>

            {demand && (
                <>
                    <p className=" mt-1 text-[11px] font-bold text-brand-deep">
                        درخواست {toman(demand.amount)} در{' '}
                        {faPlain.format(demand.num_repayments)} قسط
                    </p>
                    {/* its own line, not a «·» separator: a middle dot sitting
                        against a digit reads as an extra zero at phone size */}
                    {demand.waiting_days > 0 && (
                        <p className=" text-[10px] text-text-secondary">
                            {faPlain.format(demand.waiting_days)} روز در انتظار
                        </p>
                    )}
                </>
            )}

            {candidate.overdue_count > 0 && (
                <p className=" mt-1.5 rounded-lg bg-danger-soft px-2 py-1 text-[11px] font-bold text-danger-on">
                    {faPlain.format(candidate.overdue_count)} قسط معوق —{' '}
                    {faFull.format(candidate.overdue_amount)} تومان، تا{' '}
                    {faPlain.format(candidate.max_days_overdue)} روز تاخیر
                </p>
            )}
        </div>
    )
}
